import { useCallback, useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebaseConfig";

// Default httpsCallable timeout is 70s — but distillation can legitimately
// take 2-4 minutes when the user has 20+ samples or the AI provider is
// slow. The Cloud Function itself has a 540s ceiling, so we mirror that
// on the client side. Without this, the browser closes the connection
// before the function returns and the user sees "training failed" even
// though the function quietly finishes a minute later.
const distillCallable = httpsCallable(functions, "distillVoiceDNA", {
  timeout: 540000, // 9 min — matches functions runWith({ timeoutSeconds: 540 })
});

// Centralized distillation trigger. Tracks pending state per cloneId.
export const useDistillation = (userId) => {
  const [pendingId, setPendingId] = useState(null);
  const [error, setError] = useState(null);

  const distill = useCallback(
    async (cloneId) => {
      if (!userId || !cloneId) return null;
      setPendingId(cloneId);
      setError(null);
      try {
        // Pass userId so the backend can resolve the correct workspace path
        // when the caller is operating inside a switched team.
        const result = await distillCallable({ cloneId, userId });
        return result.data;
      } catch (err) {
        console.warn("[useDistillation] failed:", err);
        setError(err.message || "Training failed");
        return null;
      } finally {
        setPendingId(null);
      }
    },
    [userId],
  );

  return { distill, pendingId, error, isPending: (id) => pendingId === id };
};

// Auto-trigger: when sample count rises and clone is idle (not currently
// training), kick off a distillation after `delayMs` of no further changes.
export const useAutoDistillTrigger = ({
  userId,
  cloneId,
  sampleCount,
  status,
  hasMinSamples,
  hasExistingDna,
  onTrigger,
  delayMs = 60000,
}) => {
  const lastSeenCount = useRef(sampleCount);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!userId || !cloneId) return;
    // Only auto-train when:
    // 1. user has accumulated enough samples
    // 2. sample count actually changed (added/removed something)
    // 3. clone is not currently training
    const countChanged = sampleCount !== lastSeenCount.current;
    lastSeenCount.current = sampleCount;
    if (!hasMinSamples) return;
    if (status === "training") return;
    if (!countChanged && hasExistingDna) return;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onTrigger?.(cloneId);
    }, delayMs);

    return () => clearTimeout(debounceRef.current);
  }, [
    userId,
    cloneId,
    sampleCount,
    status,
    hasMinSamples,
    hasExistingDna,
    onTrigger,
    delayMs,
  ]);
};
